import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ShippingCompanyCard from "./ShippingCompanyCard";
import FeesCard from "./FeesCard";
import AdditionalInfoCard from "./AdditionalInfoCard";
import { useEffect, useState } from "react";
import {
  useAssignDeliveryComapnyMutation,
  useCreateMapiShipOrderAdminMutation,
  useLazyGetWalletBalanceQuery,
} from "src/_api/mapitShipApi";
import ChargeBalanceDialog from "../../../../components/ChargeBalanceDialog";
import { LoadingButton } from "@mui/lab";
import { useSocket } from "src/_contexts/socket";
import { useSelector } from "react-redux";
import { ROLES } from "src/constants";
import ConfirmPasswordDialogAdmin from "src/_modules/common/Dialogs/ConfirmPasswordDialogAdmin";

// import { useNavigate } from 'react-router-dom'

export default function DeliveryCompanyDetailsDialog({
  open,
  onClose,
  selectedCompany,
  orderId,
  onAssignedSuccess,
}) {
  const { t } = useTranslation();
  // const navigate = useNavigate()
  const [openAdminDialog, setOpenAdminDialog] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [openBalanceDialog, setOpenBalanceDialog] = useState(false);
  // Covers the whole confirm flow (balance lookup + assign) so the button stays
  // disabled while the balance is being resolved.
  const [submitting, setSubmitting] = useState(false);
  const [getWalletBalance, { data: wallet, isLoading: walletLoading }] =
    useLazyGetWalletBalanceQuery({});
  const { profile } = useSelector((state) => state.entities.profile);
  // Balance already resolved app-wide (App.js). Used as a fallback so a cold or
  // failed wallet request is never mistaken for an empty wallet.
  const { credits } = useSelector((state) => state.entities.creditsAndSubscription);

  useEffect(() => {
    if (open) {
      // Trigger with {} so this shares the same cache entry as the rest of the
      // app (App.js / layout header) instead of creating a separate one.
      getWalletBalance({});
    }
  }, [open]);

  // Returns the balance, or null when it cannot be determined.
  const resolveWalletBalance = async () => {
    try {
      const freshWallet = await getWalletBalance({}).unwrap();
      if (Number.isFinite(Number(freshWallet?.balance))) {
        return Number(freshWallet.balance);
      }
    } catch (error) {
      console.error(error);
    }
    if (Number.isFinite(Number(wallet?.balance))) return Number(wallet.balance);
    if (Number.isFinite(Number(credits))) return Number(credits);
    return null;
  };

  const [
    assignDeliveryComapny,
    {
      isLoading: isLoadingMerchant,
      isSuccess: isSuccessMerchant,
      error: errorMerchant,
      reset: resetMerchant,
    },
  ] = useAssignDeliveryComapnyMutation();
  const [
    assignDeliveryComapnyAdmin,
    {
      isLoading: isLoadingAdmin,
      isSuccess: isSuccessAdmin,
      error: errorAdmin,
      reset: resetAdmin,
    },
  ] = useCreateMapiShipOrderAdminMutation();

  const loading = isLoadingAdmin || isLoadingMerchant;
  const error = errorMerchant || errorAdmin;
  const success = isSuccessAdmin || isSuccessMerchant;

  const handleSuccess = () => {
    if (onAssignedSuccess) onAssignedSuccess(orderId);
    setTimeout(() => {
      onClose();
    }, 1000);
  };

  const onSubmit = async (adminPassword) => {
    try {
      setSubmitting(true);
      if (profile?.role === ROLES.admin) {
        await assignDeliveryComapnyAdmin({
          orderId: orderId,
          deliveryOption: selectedCompany,
          password: adminPassword,
        }).unwrap();
        handleSuccess()
      } else {
        const balance = await resolveWalletBalance();
        const price = Number(selectedCompany?.price);

        // Only block when we actually know the balance is short. If the balance
        // could not be read, let the server decide instead of asking the
        // merchant to recharge a wallet that may already be funded.
        if (balance !== null && Number.isFinite(price) && balance < price) {
          setOpenBalanceDialog(true);
          return;
        }

        await assignDeliveryComapny({
          orderId: orderId,
          deliveryOption: selectedCompany,
        }).unwrap();
        // on success notify parent to remove the order from the list
        handleSuccess()
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!open) {
      resetMerchant();
      resetAdmin();
    }
  }, [open]);

  const { socket } = useSocket();

  useEffect(() => {
    if (!socket) return;

    const onBalanceAdded = () => {
      getWalletBalance({});
      setOpenBalanceDialog(false);
    };

    socket.on("balanceAdded", onBalanceAdded);
    return () => socket.off("balanceAdded", onBalanceAdded);
  }, [socket]);

  return (
    <>
      <ChargeBalanceDialog
        open={openBalanceDialog}
        onClose={() => setOpenBalanceDialog(false)}
      />
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{t("Shipping Details")}</DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={2}>
            <ShippingCompanyCard selectedCompany={selectedCompany} />
            <FeesCard selectedCompany={selectedCompany} />
            {!!selectedCompany?.estimatedPickupDate &&
              !!selectedCompany?.estimatedDeliveryDate && (
                <AdditionalInfoCard selectedCompany={selectedCompany} />
              )}
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions>
          {success && (
            <Typography color="success.main">
              {t("Done successfully")}
            </Typography>
          )}
          {!!error && <Typography color="error">{t(error)}</Typography>}
          <Box mr="auto" />
          <LoadingButton
            disabled={success}
            loading={loading}
            onClick={onClose}
            color="primary"
            variant="contained"
            sx={{ textTransform: "none" }}
          >
            {t("Cancel")}
          </LoadingButton>
          {!!orderId && (
            <LoadingButton
              disabled={success}
              loading={loading || walletLoading || submitting}
              onClick={() => {
                if (profile.role === ROLES.admin) {
                  setOpenAdminDialog(true);
                } else {
                  onSubmit();
                }
              }}
              color="primary"
              variant="contained"
              sx={{ textTransform: "none" }}
            >
              {t("Confirm")}
            </LoadingButton>
          )}
          <ConfirmPasswordDialogAdmin
            open={openAdminDialog}
            title={
              "Please enter your password to be able to create shipment"
            }
            password={adminPassword}
            loading={loading}
            onPasswordChange={setAdminPassword}
            onClose={() => {
              setOpenAdminDialog(false);
              setAdminPassword("");
            }}
            onConfirm={() => {
              onSubmit(adminPassword);
              setOpenAdminDialog(false);
            }}
          />
        </DialogActions>
      </Dialog>
    </>
  );
}
